// Field Validator Factory - creates appropriate validators based on field type and config
import type {FormField} from '../../types/form-builder.types';
import type {Validator} from './types';
import {ValidatorChain} from './validator-chain';
import {
  AddressValidator,
  ColorValidator,
  CountryValidator,
  CurrencyValidator,
  EmailValidator,
  FileTypeValidator,
  ImageChoiceValidator,
  MatrixValidator,
  MaxLengthValidator,
  MinLengthValidator,
  NumberRangeValidator,
  PatternValidator,
  PhoneValidator,
  RankingValidator,
  RequiredValidator,
  SignatureValidator,
  UrlValidator
} from './validators';

/**
 * Factory function to create validators based on field configuration
 * Implements Factory Pattern
 */
export class FieldValidatorFactory {
  /**
   * Create a validator chain for a specific field
   */
  static createValidatorForField(field: FormField): Validator {
    const chain = new ValidatorChain();
    const config = field.config;

    // Required validation (always first)
    if (config.required) {
      chain.add(new RequiredValidator());
    }

    // Type-specific validators
    switch (field.type) {
      case 'email':
        chain.add(new EmailValidator());
        break;

      case 'url':
        chain.add(new UrlValidator());
        break;

      case 'phone':
      case 'tel':
        chain.add(new PhoneValidator());
        break;

      case 'number':
      case 'range':
        // Only add validator if at least one value is not null/undefined
        if ((config.min !== undefined && config.min !== null) ||
            (config.max !== undefined && config.max !== null)) {
          const min = (config.min !== undefined && config.min !== null) ? config.min : undefined;
          const max = (config.max !== undefined && config.max !== null) ? config.max : undefined;
          chain.add(new NumberRangeValidator(min, max));
        }
        break;

      case 'color':
        chain.add(new ColorValidator());
        break;

      case 'currency':
        // Add currency validator with min/max if specified
        if ((config.min !== undefined && config.min !== null) ||
            (config.max !== undefined && config.max !== null)) {
          const min = (config.min !== undefined && config.min !== null) ? config.min : undefined;
          const max = (config.max !== undefined && config.max !== null) ? config.max : undefined;
          chain.add(new CurrencyValidator(min, max));
        } else {
          chain.add(new CurrencyValidator());
        }
        break;

      case 'address':
        // Address field requires all sub-fields if the field itself is required
        if (config.required) {
          chain.add(new AddressValidator());
        }
        break;

      case 'country':
        chain.add(new CountryValidator());
        break;

      case 'signature':
        if (config.required) {
          chain.add(new SignatureValidator());
        }
        break;

      case 'ranking':
        // Validate all items are ranked if required
        if (config.required && (config as any).options) {
          chain.add(new RankingValidator((config as any).options.length));
        }
        break;

      case 'image-choice':
        // Validate selection count for image choice
        const minSelections = (config as any).minSelections;
        const maxSelections = (config as any).maxSelections;
        if (minSelections !== undefined || maxSelections !== undefined) {
          chain.add(new ImageChoiceValidator(minSelections, maxSelections));
        }
        break;

      case 'date':
      case 'datetime-local':
      case 'time':
        // Could add date range validator if min/max dates are specified
        // chain.add(new DateRangeValidator(config.minDate, config.maxDate));
        break;

      case 'file':
      case 'file-upload':
      case 'image':
        if (config.accept) {
          chain.add(new FileTypeValidator(config.accept));
        }
        // Could add file size validator
        // if (config.maxFileSize) {
        //   chain.add(new FileSizeValidator(config.maxFileSize * 1024 * 1024, config.maxFileSize));
        // }
        break;

      case 'checkbox':
      case 'checkbox-group':
      case 'multi-select':
        // Array length validation for multi-select fields
        // TODO: Add minItems/maxItems to FieldConfig type if needed
        // if (config.minItems !== undefined || config.maxItems !== undefined) {
        //   chain.add(new ArrayLengthValidator(config.minItems, config.maxItems));
        // }
        break;

      case 'matrix':
      case 'rating-matrix':
        // Matrix field validation - check if requireAllRows is enabled
        if ((config as any).requireAllRows) {
          chain.add(new MatrixValidator((config as any).rows, true));
        }
        break;
    }

    // Text length validators (for text-based fields)
    if (this.isTextBasedField(field.type)) {
      if (config.minLength !== undefined && config.minLength !== null) {
        chain.add(new MinLengthValidator(config.minLength));
      }
      if (config.maxLength !== undefined && config.maxLength !== null) {
        chain.add(new MaxLengthValidator(config.maxLength));
      }
    }

    // Pattern validator (custom regex)
    if (config.pattern) {
      chain.add(new PatternValidator(config.pattern));
    }

    // Custom validation rules from config
    if (config.validation && Array.isArray(config.validation)) {
      config.validation.forEach(rule => {
        const validator = this.createValidatorFromRule(rule);
        if (validator) {
          chain.add(validator);
        }
      });
    }

    return chain;
  }

  /**
   * Create validator from validation rule
   */
  private static createValidatorFromRule(rule: any): Validator | null {
    switch (rule.type) {
      case 'required':
        return new RequiredValidator();

      case 'email':
        return new EmailValidator();

      case 'url':
        return new UrlValidator();

      case 'pattern':
        return new PatternValidator(rule.value, rule.message);

      case 'min':
        return new NumberRangeValidator(rule.value, undefined);

      case 'max':
        return new NumberRangeValidator(undefined, rule.value);

      default:
        return null;
    }
  }

  /**
   * Check if field type is text-based
   */
  private static isTextBasedField(type: string): boolean {
    return [
      'text',
      'textarea',
      'email',
      'url',
      'tel',
      'phone',
      'password',
      'search'
    ].includes(type);
  }
}

